import { useI18n } from "vue-i18n";
import { useChartsStore } from "~/store/charts.ts";
import { useSearchStore } from "~/store/search.ts";
import { useChartTemplate } from "~/composables/useChartTemplate";
import AnalyticsChartAttributes from "~/types/attributes/AnalyticsChartAttributes";
import {
  AddChartPayload,
  AnalyticsChart,
  ChartSizeChangePayload,
} from "~/types/ChartsType";

export function useChartOperations() {
  const { t } = useI18n();

  const chartsStore = useChartsStore();
  const savedSearchStore = useSearchStore();
  const { shouldSaveTemplate, findSelectedTemplate, saveTemplate } =
    useChartTemplate();

  // new position for added new charts
  const determineNewPosition = (chartsList?: any[]) =>
    chartsList?.length ? chartsList.length + 1 : 1;

  const saveAnalyticsChart = async (payload: AddChartPayload) => {
    const templateId =
      savedSearchStore.selectedSearch.analytics_chart_template_id;
    const chartsList = chartsStore.analyticsCharts.get(String(templateId));

    const newPosition = determineNewPosition(chartsList);

    const myAttributes: AnalyticsChartAttributes = {
      title: payload.props.chartTitle,
      tooltip: payload.props.chartTooltip,
      metric: payload.newMetricSelection.value.key,
      slice_by: payload.newSliceBySelection.value.key,
      chart_type: payload.props.chartType,
      rows: 1,
      columns: payload.props.chartSize,
      position: payload.props.position ?? newPosition,
      comparison: Boolean(savedSearchStore.selectedSearch.is_comparison),
    };

    const isNew = payload.props.dialogType === "add";

    if (templateId) {
      const resultChart = await chartsStore.saveAnalyticsChart(
        null,
        templateId,
        isNew,
        myAttributes,
        payload.chartId ?? null,
      );

      return resultChart;
    }
  };

  const addChart = async (payload: AddChartPayload, callback: Function) => {
    // Handles both Add and Edit of Charts
    let errorMessage: string | null = null;

    try {
      if (shouldSaveTemplate.value) {
        // This is a new saved search with no
        // associated template, let's make one!
        const template = findSelectedTemplate();
        if (template) {
          // Handle add/edits for new templates
          const chartArray = mutateChartArray(payload);
          await saveTemplate(template, chartArray);
          await chartsStore.fetchChartsByTemplateID();
        }
      } else {
        // Handle add/edits for an existing template
        await saveAnalyticsChart(payload);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error("creating new chart Error: ", e.message);
      }

      errorMessage = "notifications.error";
    } finally {
      if (callback) callback(payload, errorMessage);
    }
  };

  const mutateChartArray = (payload: AddChartPayload) => {
    // Deep copy the charts,
    // otherwise we risk accidental store mutation
    const chartArray = JSON.parse(
      JSON.stringify(
        chartsStore.analyticsCharts.get(
          String(chartsStore.selectedChartTemplate.id),
        ),
      ),
    );
    // The chart changes we are making currently
    const newChart: {
      id: string;
      attributes: AnalyticsChartAttributes;
      type: string;
    } = {
      id: String(payload.chartId),
      attributes: {
        title: payload.props.chartTitle,
        tooltip: payload.props.chartTooltip,
        metric: payload.newMetricSelection.value.key,
        slice_by: payload.newSliceBySelection.value.key,
        chart_type: payload.props.chartType,
        rows: 1,
        columns: payload.props.chartSize,
        position: payload.props.position ?? determineNewPosition(chartArray),
        comparison: Boolean(savedSearchStore.selectedSearch.is_comparison),
      },
      type: "analytics_chart",
    };
    // If we're editing, replace the old chart with the new attributes
    for (let i = 0; i < chartArray.length; i++) {
      if (String(chartArray[i].id) === String(payload.chartId)) {
        chartArray[i] = newChart;
        // This is a newly added chart, push it to the end
      } else if (payload.chartId === 0) {
        chartArray.push(newChart);
        break;
      }
    }
    return chartArray;
  };

  const fakePayload = (
    editingChart: AnalyticsChart,
    size: number,
    chartId: number,
  ) => {
    // Fake a payload to send to mutateChartArray
    const payload: AddChartPayload = {
      chartId,
      props: {
        chartTitle: editingChart.attributes.title,
        chartTooltip: editingChart.attributes.tooltip,
        chartType: editingChart.attributes.chart_type,
        chartSize: size,
        position: editingChart.attributes.position,
        dialogType: "edit",
      },
      newMetricSelection: {
        value: {
          key: editingChart.attributes.metric,
        },
      },
      newSliceBySelection: {
        value: {
          key: editingChart.attributes.slice_by,
        },
      },
    };
    // Get back an array of all charts, including our new mutated one
    return mutateChartArray(payload);
  };

  /**
   * Removes a chart from pinia store then saves the template if template does not exist
   * or Removes a chart directly by chart id.
   * @param chartId - id of chart to remove.
   */
  const removeAnalyticsChart = async (chartId: string) => {
    const chartsList = chartsStore.analyticsCharts.get(
      chartsStore.selectedChartTemplate?.id,
    );
    if (shouldSaveTemplate.value) {
      // remove chart and then save the template
      const template = findSelectedTemplate();
      if (template) {
        const updatedCharts = chartsList.filter(
          (c: AnalyticsChart) => chartId !== c.id,
        );
        if (template.id !== "1" && template.id !== "2") {
          chartsStore.analyticsCharts.set(
            chartsStore.selectedChartTemplate?.id,
            updatedCharts,
          );
        }

        await saveTemplate(template, updatedCharts);
      }
    } else {
      // just remove the chart, template exists
      const chartIndex = chartsList.findIndex(
        (chart: AnalyticsChart) => chart.id === chartId,
      );

      if (chartIndex > -1) {
        const result = await chartsStore.removeAnalyticsChart(chartId);
        if (result === "success") {
          chartsList.splice(chartIndex, 1);
          chartsList.forEach((chart: AnalyticsChart, index: number) => {
            chart.attributes.position = index + 1;
          });
        }
        return result;
      }
    }
  };

  const changeChartSize = ({
    chartId,
    size,
    callback,
  }: ChartSizeChangePayload) => {
    // Get the current template ID
    const templateId = chartsStore.selectedChartTemplate.id;

    // Get the current charts array
    const analyticsCharts = chartsStore.analyticsCharts.get(templateId);

    // Find the chart in the store
    const chartIndex = analyticsCharts.findIndex(
      (chart: AnalyticsChart) => String(chart.id) === String(chartId),
    );

    // Update the value of 'columns' for the chart in the store
    if (chartIndex !== -1) {
      /*
        If the template this chart belongs to is a default,
        we need to set things up such that the chart size is
        configured correctly on the initial template save.
        Saving the template is what resizes the chart. Unfortunately,
        charts on pre-saved templates behave differently, hence the else below.
      */
      let chartArray;
      // This is a default template
      if (["1", "2"].includes(String(templateId))) {
        chartArray = fakePayload(analyticsCharts[chartIndex], size, chartId);
      } else {
        analyticsCharts[chartIndex].attributes.columns = size;
      }
      // chartArray is null for existing templates
      if (callback) callback(chartArray);
    }
  };

  /**
   * Updates the position attributes of charts after drag and drop.
   *
   * Vue Draggable Next creates a two-way binding via the :list prop,
   * which means:
   * 1. When a chart is dragged and dropped, the library automatically
   *    updates the order of items in the bound array (charts)
   * 2. The visual order in the UI automatically matches the array order
   * 3. The @end event fires after reordering is complete
   *
   * Therefore, when this method runs, the charts array is already in
   * the new order matching what's displayed on screen. We just need
   * to update the position attributes to reflect this new order.
   */
  const changeChartPosition = (callback: Function) => {
    // Get the current template ID
    const templateId = chartsStore.selectedChartTemplate.id;

    // Get the current charts array
    const currentCharts = chartsStore.analyticsCharts.get(templateId);

    // Update positions based on new order
    const updatedCharts = currentCharts.map(
      (chart: AnalyticsChart, index: number) => ({
        ...chart,
        attributes: {
          ...chart.attributes,
          position: index + 1,
        },
      }),
    );

    // Update the Map with the new array
    chartsStore.analyticsCharts.set(templateId, updatedCharts);

    if (callback) callback();
  };

  /**
   * Calculates the width of a chart based on the number of columns
   * and the total width of the container.
   * Divide the usable width by the number of columns
   * and subtract the gap to get the width of each chart.**/
  const getChartWidth = (
    columns: number,
    containerWidth: number = 0,
    gapPx: number = 0,
  ) => {
    if (containerWidth === 0) {
      switch (columns) {
        case 1:
          return "w-1/3";
        case 2:
          return "w-2/3";
        case 3:
          return "w-full";
        default:
          return "w-1/3";
      }
    } else {
      let usableWidth = containerWidth;
      let totalGap = 0;
      switch (columns) {
        case 1:
          totalGap = gapPx * 2;
          usableWidth = containerWidth - totalGap;
          return Math.floor(usableWidth / 3).toFixed(2);
        case 2:
          totalGap = gapPx * 1;
          usableWidth = containerWidth - totalGap;
          return Math.floor((usableWidth / 3) * 2).toFixed(2);
        case 3:
          return usableWidth;
        default:
          totalGap = gapPx * 2;
          usableWidth = containerWidth - totalGap;
          return Math.floor(usableWidth / 3).toFixed(2);
      }
    }
  };

  const translateDefaultChartAttribute = (chartAttribute: string) => {
    return chartAttribute?.includes("analytics_chart.")
      ? t(chartAttribute)
      : chartAttribute;
  };

  return {
    saveAnalyticsChart,
    removeAnalyticsChart,
    addChart,
    changeChartSize,
    getChartWidth,
    changeChartPosition,
    translateDefaultChartAttribute,
  };
}
